/**
 * Product media carousel: a horizontal scroll-snap track driven by
 * `--prev`, `--next` and `--goto` commands (buttons use commandfor/command).
 * Thumbnails carry data-index and get aria-current synced to the visible slide.
 */
class SMediaCarousel extends HTMLElement {
  #track = null;
  #thumbs = [];
  #frame = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-carousel-track]');
    this.#thumbs = [...this.querySelectorAll('[data-carousel-thumb]')];
    if (!this.#track) return;
    this.addEventListener('command', this.#onCommand);
    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('command', this.#onCommand);
    this.#track?.removeEventListener('scroll', this.#onScroll);
    cancelAnimationFrame(this.#frame);
  }

  get #slides() {
    return this.#track ? [...this.#track.children] : [];
  }

  get #index() {
    const width = this.#track.clientWidth || 1;
    return Math.round(Math.abs(this.#track.scrollLeft) / width);
  }

  #go(index) {
    const slides = this.#slides;
    if (!slides.length) return;
    const target = (index + slides.length) % slides.length;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({ left: slides[target].offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
  }

  #onCommand = (event) => {
    if (event.command === '--prev') this.#go(this.#index - 1);
    else if (event.command === '--next') this.#go(this.#index + 1);
    else if (event.command === '--goto') this.#go(Number(event.source?.dataset.index || 0));
  };

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
  };

  #sync() {
    const current = this.#index;
    this.#thumbs.forEach((thumb, index) => {
      thumb.setAttribute('aria-current', index === current ? 'true' : 'false');
    });
  }
}

if (!customElements.get('s-media-carousel')) {
  customElements.define('s-media-carousel', SMediaCarousel);
}
